import React, { useEffect, useMemo, useRef, useState } from "react";

/* ===========================
   Constants
=========================== */
const BLOCKS_PER_CYCLE = 210_000;
const MINUTES_PER_BLOCK = 10;
const ANIMATION_DURATION = 900;

const getOrdinal = (value: number) => {
    const mod10 = value % 10;
    const mod100 = value % 100;

    if (mod10 === 1 && mod100 !== 11) return `${value}st`;
    if (mod10 === 2 && mod100 !== 12) return `${value}nd`;
    if (mod10 === 3 && mod100 !== 13) return `${value}rd`;
    return `${value}th`;
};

/* ===========================
   Props
=========================== */
interface Props {
    currentBlock: number;
    sizeRem?: number;
}

/* ===========================
   Hook (cycle calculations)
=========================== */
const useBtcCycle = (currentBlock: number) => {
    return useMemo(() => {
        const cycle =
            Math.floor(currentBlock / BLOCKS_PER_CYCLE) + 1;

        const cycleStart =
            (cycle - 1) * BLOCKS_PER_CYCLE;

        const cycleEnd =
            cycle * BLOCKS_PER_CYCLE;

        const progress =
            ((currentBlock - cycleStart) /
                BLOCKS_PER_CYCLE) *
            100;

        const remainingBlocks =
            cycleEnd - currentBlock;

        const remainingMinutes =
            remainingBlocks * MINUTES_PER_BLOCK;

        const remainingDays = Math.ceil(
            remainingMinutes / 1440
        );

        const nextHalving = new Date(
            Date.now() +
            remainingMinutes * 60 * 1000
        );

        return {
            cycle,
            progress,
            remainingDays,
            nextHalving,
        };
    }, [currentBlock]);
};

/* ===========================
   Component
=========================== */
const BtcCycleHybridGauge: React.FC<Props> = ({
    currentBlock,
    sizeRem = 11.5,
}) => {
    const data = useBtcCycle(currentBlock);

    /* ===== Animated progress ===== */
    const [animatedProgress, setAnimatedProgress] =
        useState(0);

    const prev = useRef(0);
    const frameRef = useRef<number | null>(null);

    useEffect(() => {
        const start = prev.current;
        const end = data.progress;
        const startTime = performance.now();

        const animate = (time: number) => {
            const t = Math.min(
                (time - startTime) / ANIMATION_DURATION,
                1
            );

            const eased = 1 - Math.pow(1 - t, 3);
            const value = start + (end - start) * eased;

            setAnimatedProgress(value);

            if (t < 1) {
                frameRef.current =
                    requestAnimationFrame(animate);
            } else {
                prev.current = end;
            }
        };

        frameRef.current =
            requestAnimationFrame(animate);

        return () => {
            if (frameRef.current)
                cancelAnimationFrame(frameRef.current);
        };
    }, [data.progress]);

    /* ===== Geometry ===== */
    const sizePx = sizeRem * 16;
    const radius = sizePx / 2 - 24;
    const circumference = Math.PI * radius;
    const topPadding = sizePx * 0.18;


    const progressArc =
        (animatedProgress / 100) * circumference;

    /* ===========================
       Render
    =========================== */
    return (
        <div
            style={{
                width: `${sizePx}px`,
                textAlign: "center",
                fontFamily: "Arial, sans-serif",
                color: "#000",
            }}
        >

            <svg
                width={`${sizePx}px`}
                height={`${sizePx / 1.4}px`}
                viewBox={`0 0 ${sizePx} ${sizePx / 2}`}
            >
                <g transform={`translate(${sizePx / 2}, ${radius + 16})`}>
                    {/* Title */}
                    <text
                        y={-radius - sizePx * 0.11}
                        textAnchor="middle"
                        fontSize={sizePx * 0.1}
                        fontWeight="600"
                        fill="#000"
                    >
                        Halving Cycle
                    </text>

                    {/* Background arc */}
                    <path
                        d={`
                                                    M ${-radius} 0
                                                    A ${radius} ${radius} 0 0 1 ${radius} 0
                                                  `}
                        fill="none"
                        stroke="#FFE0C2"
                        strokeWidth={10}
                    />

                    {/* Progress arc */}
                    <path
                        d={`
                                                    M ${-radius} 0
                                                    A ${radius} ${radius} 0 0 1 ${radius} 0
                                                  `}
                        fill="none"
                        stroke="#F7931A"
                        strokeWidth={10}
                        strokeDasharray={`${progressArc} ${circumference}`}
                        strokeLinecap="butt"
                    />

                    {/* Progress text */}
                    <text
                        y={-10}
                        textAnchor="middle"
                        fontSize={sizePx * 0.12}
                        fontWeight="600"
                        fill="#000"
                    >
                        {animatedProgress.toFixed(1)}%
                    </text>

                    {/* Epoch value */}
                    <text
                        y={sizePx * 0.12}
                        textAnchor="middle"
                        fontWeight="400"
                        fontStyle="italic"
                        fill="#475569"
                    >
                        {getOrdinal(data.cycle)} epoch
                    </text>

                </g>
            </svg>

            <div
                style={{
                    width: "42%",
                    height: "1px",
                    margin: "0.1rem auto 0.25rem",
                    backgroundColor: "rgba(247, 147, 26, 0.65)",
                }}
            />

            <div
                style={{
                    marginTop: "0.3rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                }}
            >
                <div>
                    <span>Days until halving: </span>
                    <span style={{ color: "#000" }}>
                        <strong>{data.remainingDays}</strong>
                    </span>
                    <br />
                    <span>Estimated Next Halving: </span>
                    <br/>
                    <span style={{ color: "#000" }}>
                        <strong>{data.nextHalving.toDateString().split(" ").slice(1).join(" ")}</strong>
                    </span>
                </div>
            </div>

            {/* Days + date
            <div style={{
                marginTop: "0.3rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
            }}>
                {data.remainingDays} days until halving
                <br />
                <strong style={{ color: "#000" }}>
                    {data.nextHalving.toDateString()}
                </strong>
            </div> */}
        </div>
    );

};

export default React.memo(BtcCycleHybridGauge);
